import { ReactElement, ReactNode, useEffect, useRef, useState } from "react";
import { intl } from "LocaleWrapper";
import { saveAs } from "file-saver";

import { useMutation, useQuery } from "@apollo/client";

import { showSnackbar, Table, Tag, TagSize } from "@cofano/cck";
import { ActionProps, ContentProps } from "@cofano/cck/Table/Table";
import { useCoastguard } from "@cofano/coastguard2-frontend-client";

import AddIcon from "@mui/icons-material/Add";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import GetAppIcon from "@mui/icons-material/GetApp";
import BlockIcon from "@mui/icons-material/Block";
import DeleteIcon from "@mui/icons-material/Delete";

import { useConfirm } from "components/hooks/useConfirm";

import { usePermission } from "imports/Utils";

import { DocumentEntity, DocumentsByEntityQuery } from "__generated__/graphql";
import { gql } from "__generated__/gql";

import DocumentUploadDialog from "./DocumentUploadDialog";
import { FormattedDate } from "utils/DateUtils";

import { DocumentLink, getDocumentStatusColor } from "components/documents/DocumentsUtils";
import { Document } from "./types/document";
import DocumentDrawer from "components/documents/DocumentDrawer";

// eslint-disable-next-line react-refresh/only-export-components
export enum DocumentOptionalColumns {
	Entity = "ENTITY",
	DocumentType = "DOCUMENT_TYPE",
	IssuedAt = "ISSUED_AT",
	ValidUntil = "VALID_UNTIL",
	Status = "STATUS",
	LastStatusUpdateOn = "LAST_STATUS_UPDATE_ON"
}

type DrawerActionProps = {
	render: (document: QueriedDocument) => ReactNode | ReactElement;
}

export type DocumentsProps = {
	entities: [Entity, ...Entity[]];
	showActions?: boolean;
	showOptionalColumns?: DocumentOptionalColumns[];
	documentTypeOptions?: string[];
	canCreate?: boolean;
	canDelete?: boolean;
	defaultEntity?: Entity;
	additionalDrawerActions?: (refetch: () => void, closeDrawer: () => void) => DrawerActionProps[];
	additionalActions?: (refetch: () => void) => ActionProps[];
	onDocumentsLoaded?: (documents: QueriedDocument[]) => void;
	onRefetch?: () => void;
	setSelection?: (selection: { id?: number }[]) => void;
	warning?: string;
};

export type Entity = {
	id: number;
	name: string;
	type: DocumentEntity,
	callId?: number | undefined;
	cargoActionId?: number | undefined;
	blockUpload?: boolean;
};

export type QueriedDocument = DocumentsByEntityQuery["documentsByEntity"][number];

const Documents = ({
	entities,
	showActions = true,
	showOptionalColumns = [],
	documentTypeOptions,
	canCreate = true,
	canDelete = true,
	defaultEntity,
	additionalActions = () => [],
	additionalDrawerActions = () => [],
	onRefetch,
	onDocumentsLoaded,
	setSelection,
	warning,
}: DocumentsProps) => {

	const { isReadOnly } = usePermission();
	const { instance } = useCoastguard();

	const confirm = useConfirm();

	const [ dialogOpen, openDialog ] = useState(false);
	const [ drawer, setDrawer ] = useState<{ open: boolean, document: QueriedDocument | null }>({ open: false, document: null });

	const tableRef = useRef<{ clearSelection: () => void; handleSelectAllSelection: (rows: ContentProps, isFullSelect: boolean) => void; } | null>(null);

	const { data, refetch, loading } = useQuery(documentsByEntityQuery, {
		variables: {
			entities: entities.map(e => ({
				entityId: e.cargoActionId ?? e.id,
				entityType: e.type,
			})),
		},
		onCompleted: (data) => {
			onDocumentsLoaded?.(data.documentsByEntity);
			if (tableRef.current?.clearSelection) {
				tableRef.current.clearSelection();
			}
		},
	});

	const [ deleteDocument ] = useMutation(deleteMutation, {
		onCompleted: () => {
			onRefetch?.();
			refetch();
			showSnackbar(intl.formatMessage({ id: "SUCCESSFUL_DELETED" }), { variant: "success" });

			if (tableRef.current?.clearSelection) {
				tableRef.current.clearSelection();
			}
		},
	});

	const [ setActive ] = useMutation(setActiveMutation, {
		onCompleted: () => {
			refetch();

			if (tableRef.current?.clearSelection) {
				tableRef.current.clearSelection();
			}
		},
	});

	const download = async({ id, filename }: Document) => {
		saveAs(await instance.fetchBlob(`/documents/download/${id}`), filename || "");
	};

	const defaultActions = [ {
		title: intl.formatMessage({ id: "DOWNLOAD" }),
		onClick: (selection: Document[]) => {

			[ ...selection ].forEach(item => download(item));

			if (tableRef.current?.clearSelection) {
				tableRef.current.clearSelection();
			}
		},
	} ];

	useEffect(() => {
		if (setSelection == null) {
			return;
		}
		const defaultSelection = data?.documentsByEntity.map(x => ({ id: x.id })) ?? [];
		if (tableRef.current?.handleSelectAllSelection) {
			tableRef.current.handleSelectAllSelection(defaultSelection, true);
			setSelection(defaultSelection);
		}
	}, [ data ]);

	// Remount upload dialog when target entity changes, prevent stale entity.
	const uploadEntity = defaultEntity ?? entities[0];

	return (
		<div>
			<Table
				id="documents"
				tableRef={tableRef}
				disableFooter={true}
				content={(data?.documentsByEntity || []).sort((a, b) => (a.created ?? 0) - (b.created ?? 0)) as ContentProps}
				contentLoading={loading}
				columns={[ {
					id: "description",
					title: intl.formatMessage({ id: "DESCRIPTION" }),
				},  {
					id: "entity",
					title: intl.formatMessage({ id: "ENTITY" }),
					width: 150,
					render: (row) => <DocumentLink document={row} />,
					disabled: !showOptionalColumns.includes(DocumentOptionalColumns.Entity),
				}, {
					id: "filename",
					title: intl.formatMessage({ id: "FILE" }),
					width: 150,
				}, {
					id: "type",
					title: intl.formatMessage({ id: "TYPE" }),
				}, {
					id: "documentType",
					title: intl.formatMessage({ id: "DOCUMENT_TYPE" }),
					width: 150,
					render: (row) => intl.formatMessage({ id: `${row.documentType}_DOCUMENT` }),
					disabled: !showOptionalColumns.includes(DocumentOptionalColumns.DocumentType),
				}, {
					id: "issuedAt",
					title: intl.formatMessage({ id: "ISSUED_AT" }),
					disabled: !showOptionalColumns.includes(DocumentOptionalColumns.IssuedAt),
				}, {
					id: "validUntil",
					title: intl.formatMessage({ id: "VALID_UNTIL" }),
					render: (row) => <FormattedDate date={row.validUntil} format="ll" withoutYear />,
					disabled: !showOptionalColumns.includes(DocumentOptionalColumns.ValidUntil),
				}, {
					id: "created",
					title: intl.formatMessage({ id: "UPLOADED" }),
					render: (row) => <FormattedDate date={row.created} withoutYear />,
				}, {
					id: "status",
					title: intl.formatMessage({ id: "STATUS" }),
					render: (row) => <Tag
						color={getDocumentStatusColor(row.status)}
						text={intl.formatMessage({ id: `DOCUMENT_STATUS_${row.status}` })}
						size={TagSize.NORMAL}
					/>,
					disabled: !showOptionalColumns.includes(DocumentOptionalColumns.Status),
				}, {
					id: "lastStatusUpdateOn",
					title: intl.formatMessage({ id: "LAST_STATUS_CHANGE" }),
					render: (row) => <FormattedDate date={row.lastStatusUpdateOn} withoutYear />,
					disabled: !showOptionalColumns.includes(DocumentOptionalColumns.LastStatusUpdateOn),
				} ]}
				//@ts-expect-error
				actions={showActions ? (
					[
						...additionalActions(refetch),
						...(canCreate && !isReadOnly ? [ ...defaultActions, {
							icon: <AddIcon />,
							onClick: () => openDialog(true),
							pinned: true,
						} ] : defaultActions) ]
				) : []}
				rowActions={!isReadOnly ? [ {
					icon: <InsertDriveFileIcon />,
					onClick: (row) => setDrawer({ open: true, document: row }),
				}, {
					icon: <GetAppIcon />,
					onClick: (row) => download(row),
				}, {
					icon: <BlockIcon />,
					onClick: (row) => {
						setActive({
							variables: {
								id: row.id,
								active: false,
							},
						});
					},
					disabled: (row) => row.documentType !== "SDS" || !row.active,
				}, {
					icon: <DeleteIcon />,
					onClick: (row) => {
						confirm({
							type: "DELETE",
						}).then(() => {
							deleteDocument({
								variables: {
									id: row.id,
								},
							});
						});
					},
					hidden: !canDelete,
				} ]  : undefined}
				selectSettings={{
					isFullSelectEnabled: true,
					selectionEnabled: true,
					onSelect: (selectedRows) => setSelection?.(selectedRows),
				}} />

			<DocumentDrawer
				open={drawer.open}
				onClose={() => setDrawer({ open: false, document: null })}
				url={`documents/download/${drawer.document?.id}`}
				previewUrl={`documents/preview/${drawer.document?.id}`}
				actions={additionalDrawerActions(refetch, () => setDrawer({ open: false, document: null })).map(action => action.render(drawer.document!))}
			/>

			<DocumentUploadDialog
				open={dialogOpen}
				onClose={() => openDialog(false)}
				entities={entities.filter(x => !x.blockUpload)}
				documentTypeOptions={documentTypeOptions}
				defaultEntity={defaultEntity}
				key={uploadEntity?.cargoActionId ?? uploadEntity?.id}
				warning={warning}
				onUploaded={() => {
					onRefetch?.();
					refetch();
				}} />
		</div>
	);
};

export const documentsByEntityQuery = gql(`#graphql
	query documentsByEntity($entities: [DocumentQueryInput!]!) {
		documentsByEntity(entities: $entities) {
			...documentFragment
		}
	}
`);

const deleteMutation = gql(`#graphql
	mutation deleteDocument($id: Int!) {
		deleteDocument(id: $id)
	}
`);

const setActiveMutation = gql(`#graphql
	mutation setDocumentActive($id: Int!, $active: Boolean!) {
		setDocumentActive(id: $id, active: $active) {
			id
		}
	}
`);

export default Documents;
